import { useState, useEffect } from 'react'
import { User, Bell, Key, Trash2, Save } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { settingsApi, authApi } from '../services/api'
import { Tabs, Spinner } from '../components/common'

export default function Settings() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState('profile')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  )

  // Profile state
  const [profile, setProfile] = useState({
    name: user?.name || '',
    company: user?.company || '',
  })

  // Notification state
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    slack: false,
    phone_number: '',
  })

  // Password state
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  })

  useEffect(() => {
    if (activeTab === 'notifications') {
      loadNotifications()
    }
  }, [activeTab])

  const loadNotifications = async () => {
    try {
      const data = await settingsApi.getNotifications()
      setNotifications({
        email: data.email,
        sms: data.sms,
        slack: data.slack,
        phone_number: data.phone_number || '',
      })
    } catch {
      // Use defaults
    }
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage(null)

    try {
      await settingsApi.updateProfile(profile)
      setMessage({ type: 'success', text: 'Profile updated successfully' })
    } catch {
      setMessage({ type: 'error', text: 'Failed to update profile' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleNotificationsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage(null)

    try {
      await settingsApi.updateNotifications(notifications)
      setMessage({ type: 'success', text: 'Notification settings updated' })
    } catch {
      setMessage({ type: 'error', text: 'Failed to update notifications' })
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match' })
      return
    }

    if (passwords.new.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      await authApi.changePassword(passwords.current, passwords.new)
      setMessage({ type: 'success', text: 'Password changed successfully' })
      setPasswords({ current: '', new: '', confirm: '' })
    } catch {
      setMessage({ type: 'error', text: 'Failed to change password' })
    } finally {
      setIsLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'security', label: 'Security' },
    { id: 'danger', label: 'Danger Zone' },
  ]

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400">Manage your account preferences</p>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Message */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
              : 'bg-red-500/20 text-red-400 border border-red-500/50'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="card space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-white">Profile Settings</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="input opacity-50 cursor-not-allowed"
              />
              <p className="text-xs text-slate-500 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Company
              </label>
              <input
                type="text"
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                className="input"
              />
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          </form>
        )}

        {activeTab === 'notifications' && (
          <form onSubmit={handleNotificationsSubmit} className="card space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="w-5 h-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-white">Notification Preferences</h3>
            </div>

            <p className="text-slate-400">
              Choose how you want to be notified when someone views your videos.
            </p>

            <div className="space-y-4 pt-2">
              <label className="flex items-center justify-between">
                <div>
                  <span className="text-white">Email Notifications</span>
                  <p className="text-sm text-slate-400">
                    Receive view notifications via email
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.email}
                  onChange={(e) =>
                    setNotifications({ ...notifications, email: e.target.checked })
                  }
                  className="rounded border-slate-600 bg-slate-700 text-primary-500"
                />
              </label>

              <label className="flex items-center justify-between">
                <div>
                  <span className="text-white">SMS Notifications</span>
                  <p className="text-sm text-slate-400">
                    Receive view notifications via text message
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.sms}
                  onChange={(e) =>
                    setNotifications({ ...notifications, sms: e.target.checked })
                  }
                  className="rounded border-slate-600 bg-slate-700 text-primary-500"
                />
              </label>

              {notifications.sms && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={notifications.phone_number}
                    onChange={(e) =>
                      setNotifications({ ...notifications, phone_number: e.target.value })
                    }
                    placeholder="+1234567890"
                    className="input"
                  />
                </div>
              )}

              <label className="flex items-center justify-between">
                <div>
                  <span className="text-white">Slack Notifications</span>
                  <p className="text-sm text-slate-400">
                    Receive view notifications in Slack
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.slack}
                  onChange={(e) =>
                    setNotifications({ ...notifications, slack: e.target.checked })
                  }
                  className="rounded border-slate-600 bg-slate-700 text-primary-500"
                />
              </label>
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
              Save Preferences
            </button>
          </form>
        )}

        {activeTab === 'security' && (
          <form onSubmit={handlePasswordSubmit} className="card space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Key className="w-5 h-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-white">Change Password</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="input"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="input"
                required
              />
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? <Spinner size="sm" /> : <Key className="w-4 h-4" />}
              Change Password
            </button>
          </form>
        )}

        {activeTab === 'danger' && (
          <div className="card border-red-500/50">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h3 className="text-lg font-semibold text-white">Danger Zone</h3>
            </div>

            <p className="text-slate-400 mb-4">
              Once you delete your account, there is no going back. All your videos,
              tracking links, and analytics data will be permanently deleted.
            </p>

            <button
              onClick={() => {
                if (
                  confirm(
                    'Are you sure you want to delete your account? This cannot be undone.'
                  )
                ) {
                  // TODO: Implement account deletion
                  alert('Account deletion not implemented yet')
                }
              }}
              className="btn-danger"
            >
              <Trash2 className="w-4 h-4" />
              Delete Account
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
